// src/components/CustomizationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { getTemplate } from '../utils/supabaseUtils';
import { exportTemplateCustomized as exportTemplate } from '../utils/cesdkCustomizerUtils';
import { useCustomizeCESDK } from '../hooks/useCustomizeCESDK';
import { 
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button as ChakraButton, Image as ChakraImage, useDisclosure,
  Box, Flex, Heading, Text, FormControl, FormLabel, Input, Stack, SimpleGrid, Spinner, useColorModeValue, VStack
} from '@chakra-ui/react';
import { loadTemplate, getAllBlocksInScene } from '../utils/cesdkUtils';
import { addWatermark } from '../utils/imageUtils';
import { useCart } from '../context/CartContext';
import { removeBackground } from '../services/removeBgService';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';

// Fun AI loading messages
const LOADING_MESSAGES = [
  'Infusing your design with swagger',
  'Coaching the pixels into formation',
  'Drafting your highlight reel',
  'Syncing playbook with creativity',
  'Spinning up some sports magic',
  'Tailoring greatness just for you'
];

const CustomizationPage = () => {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const uploadedImageURL = location.state?.uploadedImageURL;

  const [user, setUser] = useState(null);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [mediaDayImages, setMediaDayImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({});
  const [placeholders, setPlaceholders] = useState([]);
  const [exportURL, setExportURL] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templateThumbnail, setTemplateThumbnail] = useState(null);
  const [previewURL, setPreviewURL] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [saveAction, setSaveAction] = useState('save'); // 'save' or 'saveAndCart'
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef();

  // CESDK initialisation (isolated to this component)
  const editorContainerRef = useRef(null);
  const {
    cesdk,
    status: cesdkStatus,
    initialize
  } = useCustomizeCESDK();

  // Cart functionality
  const { addToCart } = useCart();

  // Modal control for preview
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Authentication and initial data fetch
  useEffect(() => {
    const fetchSessionAndData = async () => {
      // Special case: Handle "dynamic" templateId
      if (templateId === 'dynamic') {
        console.log('Detected dynamic template request, redirecting to dynamic generation demo');
        sessionStorage.setItem('dynamicRedirectMessage', 'true');
        navigate('/test-dynamic-generation');
        return;
      }
      
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        navigate('/login');
        return;
      }
      setUser(data.session.user);

      // Fetch active user uploads from DB to respect user selections (same logic as Dashboard)
      const { data: uploads, error: uploadsErr } = await supabase
        .from('user_uploads')
        .select('file_url, is_primary')
        .eq('user_id', data.session.user.id)
        .eq('status', 'active');

      if (!uploadsErr && uploads?.length) {
        const urls = [...new Set(uploads.map(u => u.file_url).filter(url => !url.includes('picsum.photos')))];
        setMediaDayImages(urls);

        // Determine primary image
        const primary = uploads.find(u => u.is_primary);
        const firstUrl = primary ? primary.file_url : urls[0];
        setPrimaryImage(firstUrl);
        setSelectedImage(firstUrl);
      } else {
        console.warn('No active uploads found for user or error:', uploadsErr);
      }

      // Fetch template data
      try {
        const templateData = await getTemplate(templateId);
        if (!templateData) throw new Error('Template not found');
        setPlaceholders(templateData.placeholder_data || []);
        setTemplateThumbnail(templateData.thumbnail_url || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSessionAndData();
  }, [templateId, navigate]);

  // Initialise CESDK once the container is available and the user is loaded
  useEffect(() => {
    if (!editorContainerRef.current || !user) return;
    if (cesdk) return; // already initialised
    (async () => {
      try {
        await initialize(editorContainerRef.current, { userId: user.id });
      } catch (e) {
        console.error('CESDK init failed in CustomizationPage:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorContainerRef, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (action = 'save') => {
    setLoading(true);
    setError(null);
    setSaveAction(action);

    try {
      const replacementData = { ...formData };

      // Replace 'source' or 'person' placeholders with the selected image
      const imagePlaceholder = placeholders.find(p => ['source', 'person'].includes(p.name.toLowerCase()));
      if (imagePlaceholder) {
        replacementData[imagePlaceholder.name] = selectedImage || primaryImage || uploadedImageURL;
      }

      // Fallback mappings (common placeholder names)
      replacementData['Person'] = selectedImage || primaryImage || uploadedImageURL;
      replacementData['Source'] = selectedImage || primaryImage || uploadedImageURL;

      if (!cesdk) {
        throw new Error('Editor not ready yet. Please wait a moment and try again.');
      }

      const exportUrl = await exportTemplate(cesdk, templateId, replacementData);
      if (!exportUrl) throw new Error('Failed to create template');
      
      setExportURL(exportUrl);
      
      // If user chose "Save & Add to Cart", add the item to cart
      if (action === 'saveAndCart') {
        // Create a more descriptive name based on customization
        let designName = 'Custom Design';
        const textValues = Object.values(formData).filter(val => val && val.trim());
        if (textValues.length > 0) {
          // Use the first text value (like player name) to make it more personal
          designName = `Custom Design - ${textValues[0]}`;
        }
        
        const cartItem = {
          id: `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique ID
          name: designName,
          template_name: designName,
          export_url: exportUrl,
          display_url: exportUrl, // Use the exported URL (already watermarked from the function)
          url: exportUrl, // Some cart components expect 'url' property
          template_id: templateId,
          type: 'custom_design',
          created_at: new Date().toISOString()
        };
        
        // Add to cart
        const added = addToCart(cartItem);
        
        if (added) {
          console.log('Successfully added custom design to cart:', designName);
        }
      }
      
      onOpen();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildReplacementData = () => {
    const data = { ...formData };
    if (selectedImage) {
      data['Person'] = selectedImage;
      data['Source'] = selectedImage;
    }
    return data;
  };

  const generatePreview = async (replacementData) => {
    if (!cesdk || cesdkStatus !== 'ready' || !replacementData) return;
    setPreviewLoading(true);
    // pick fun message
    setLoadingMessage(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
    try {
      // Ensure template loaded only first time
      if (!cesdk._customizerTemplateLoaded) {
        await loadTemplate(cesdk, templateId);
        cesdk._customizerTemplateLoaded = true;
      }

      const scene = cesdk.engine.scene.get();
      const blocks = getAllBlocksInScene(cesdk, scene);

      // Diagnostic: log all blocks in the scene once per preview generation
      try {
        console.log('CustomizationPage preview: blocks in scene:', blocks.map(id => {
          try {
            return {
              id,
              name: cesdk.engine.block.getName(id),
              type: cesdk.engine.block.getType(id),
              hasFill: (() => {
                try {
                  return !!cesdk.engine.block.getFill(id);
                } catch (e) {
                  return false;
                }
              })()
            };
          } catch (e) {
            return { id, error: e.message };
          }
        }));
        
        // Also log what we're trying to replace
        console.log('CustomizationPage preview: replacement data:', replacementData);
        console.log('CustomizationPage preview: placeholders from template:', placeholders);
      } catch (logErr) {
        console.warn('Could not log blocks:', logErr);
      }

      for (const [key, value] of Object.entries(replacementData)) {
        // Skip empty values
        if (!value || value === '') continue;
        
        const blockId = blocks.find((b) => {
          try {
            return cesdk.engine.block.getName(b) === key;
          } catch (e) {
            return false;
          }
        });
        
        if (!blockId) {
          console.log(`No block found with name: ${key}`);
          continue;
        }
        
        let type;
        try {
          type = cesdk.engine.block.getType(blockId);
        } catch (e) {
          console.warn(`Failed to get type for block ${blockId}:`, e.message);
          continue;
        }

        if (type === '//ly.img.ubq/text') {
          try { cesdk.engine.block.setString(blockId, 'text/text', value); } catch (e) {}
        } else if (type === '//ly.img.ubq/graphic' && typeof value === 'string' && value.startsWith('http')) {
          // CESDK 1.52.0 compatible image replacement logic
          try {
            console.log(`Replacing image for block: ${cesdk.engine.block.getName(blockId)} (${blockId})`);
            
            // Check if block already has a fill
            let fillId = null;
            try {
              fillId = cesdk.engine.block.getFill(blockId);
            } catch (e) {
              // Block doesn't have a fill, we'll create one
            }
            
            // If no fill exists, create a new image fill
            if (!fillId) {
              console.log(`Creating new image fill for block ${blockId}`);
              fillId = cesdk.engine.block.createFill('//ly.img.ubq/fill/image');
              cesdk.engine.block.setFill(blockId, fillId);
              
              // Verify fill was attached
              try {
                const verifyFill = cesdk.engine.block.getFill(blockId);
                if (!verifyFill) {
                  console.warn(`Failed to attach fill to block ${blockId}. Skipping.`);
                  continue;
                }
                fillId = verifyFill;
              } catch (e) {
                console.warn(`Failed to verify fill attachment for block ${blockId}:`, e.message);
                continue;
              }
            }
            
                         // Set image using legacy API to avoid dimension issues  
             try {
               // Use legacy imageFileURI which doesn't require width/height and preserves natural aspect ratio
               cesdk.engine.block.setString(fillId, 'fill/image/imageFileURI', value);
               console.log(`Set image via imageFileURI for block ${blockId}`);
             } catch (imageError) {
               console.warn(`Failed to set image for block ${blockId}:`, imageError.message);
               continue;
             }
            
                         // Set image properties using CESDK 1.52.0 content fill mode (on the block, not the fill)
             try {
               // CESDK 1.52.0: Use contentFill/mode 'Fit' to match template creation settings
               cesdk.engine.block.setEnum(blockId, 'contentFill/mode', 'Fit');
               console.log(`Set contentFill/mode to 'Fit' for block ${blockId}`);
             } catch (e) {
               console.log(`Could not set contentFill/mode for block ${blockId} - using default`);
             }
             
             console.log(`Successfully configured image properties for block ${blockId}`);
            
            
          } catch (blockError) {
            console.warn(`Error processing graphic block ${blockId} (${cesdk.engine.block.getName(blockId)}):`, blockError.message);
            continue;
          }
        }
      }

      // Export at good quality for preview but not full resolution
      const exportBlob = await cesdk.engine.block.export(scene, 'image/png', {
        targetWidth: 1080,  // Good quality but not full 1920
        targetHeight: 600,  // Good quality but not full 1080
        dpi: 72,
        exportArea: 'design'
      });

      // Add minimal, quality-focused watermarking
      try {
        let watermarkedBlob = exportBlob;
        
        // Watermark 1: Top corner brand
        watermarkedBlob = await addWatermark(watermarkedBlob, {
          text: "PlayMaker Pics",
          position: 3, // Top right
          opacity: 0.8,
          fontSize: "40px", // Doubled from 20px
          color: "rgba(0, 0, 0, 0.9)" // Dark/black for prominence
        });
        
        // Watermark 2: Center subtle brand
        watermarkedBlob = await addWatermark(watermarkedBlob, {
          text: "PlayMaker Pics",
          position: 5, // Center
          opacity: 0.5,
          fontSize: "64px", // Doubled from 32px
          color: "rgba(0, 0, 0, 0.7)" // Dark but still subtle
        });
        
        // Legal claim at bottom
        watermarkedBlob = await addWatermark(watermarkedBlob, {
          text: "© PlayMaker Pics - Preview Only",
          position: 8, // Bottom center
          opacity: 0.9,
          fontSize: "28px", // Doubled from 14px
          color: "rgba(0, 0, 0, 0.9)" // Dark for clear legal notice
        });
        
        const objectUrl = URL.createObjectURL(watermarkedBlob);
        setPreviewURL((prev) => { if (prev) URL.revokeObjectURL(prev); return objectUrl; });
      } catch (watermarkError) {
        console.warn('Failed to add watermark to preview, using original:', watermarkError);
        const objectUrl = URL.createObjectURL(exportBlob);
        setPreviewURL((prev) => { if (prev) URL.revokeObjectURL(prev); return objectUrl; });
      }
    } catch (err) {
      console.error('Live preview generation failed:', err);
     } finally {
      setPreviewLoading(false);
    }
  };

  // Trigger preview when selectedImage or formData changes
  useEffect(() => {
    if (!placeholders.length) return;
    const data = buildReplacementData();
    generatePreview(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, formData]);

  // Inject typing animation CSS once
  useEffect(() => {
    const styleId = 'typing-effect-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
      @keyframes typingEffect { from { width: 0 } to { width: 100% } }
      @keyframes caretBlink { 50% { border-color: transparent; } }
      .typing-effect {
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        border-right: 2px solid #3182ce;
        animation: typingEffect 2.5s steps(40, end), caretBlink 0.8s step-end infinite;
      }`;
      document.head.appendChild(style);
    }
  }, []);

  // Handle new user upload from Customize page
  const handleCustomizeUpload = async (file) => {
    try {
      if (!file) return;
      // Basic validation
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Unsupported file type. Please upload JPG, PNG, GIF, or WEBP.');
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        alert('File too large. Maximum 15MB.');
        return;
      }

      setUploadingImage(true);

      // 1. Remove background
      const processedBlob = await removeBackground(file);

      // 2. Upload to Supabase storage
      const { data: sessionData } = await supabase.auth.getSession();
      const currentUser = sessionData?.session?.user;
      if (!currentUser) throw new Error('Not authenticated');

      const fileName = `${currentUser.id}_${Date.now()}_${Math.random().toString(36).substring(2,10)}.png`;
      const filePath = `user_uploads/${currentUser.id}/${fileName}`;

      const processedFile = new File([processedBlob], fileName, { type: 'image/png' });

      const { error: uploadErr } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/png'
        });

      if (uploadErr) throw uploadErr;

      const { data: { publicUrl } } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath);

      // 3. Insert into user_uploads table with custom upload_type
      await supabase.from('user_uploads').insert({
        user_id: currentUser.id,
        file_url: publicUrl,
        is_primary: false,
        upload_type: 'user_uploads:customize',
        status: 'active',
        file_variant: 'processed',
        created_at: new Date().toISOString()
      });

      // 4. Update UI – prepend to mediaDayImages and select it
      setMediaDayImages((prev) => [publicUrl, ...prev]);
      setSelectedImage(publicUrl);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(err.message || 'Upload failed, please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  if (!user) return null;

  const cardBg = useColorModeValue('whiteAlpha.800', 'whiteAlpha.100');

  return (
    <Box minH="100vh" bg="gray.50" pt={4} pb={8}>
      {/* Mobile-First Header */}
      <Box bg="white" boxShadow="sm" mb={6} px={4} py={4}>
        <Flex align="center" justify="space-between" maxW="7xl" mx="auto">
          <ChakraButton
            as={RouterLink}
            to="/templates"
            variant="ghost"
            size="sm"
            leftIcon={<ArrowBackIcon />}
            colorScheme="blue"
            mr={4}
          >
            Back to AI Designs
          </ChakraButton>
          <Box>
            <Heading size="lg" color="gray.800">Create Your Design</Heading>
            <Text color="gray.600" fontSize="sm">Customize this template with your photos and details</Text>
          </Box>
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize="xs" color="gray.500">Step 2 of 2</Text>
          </Box>
        </Flex>
      </Box>

      {error && (
        <Box mx={4} mb={4}>
          <Box bg="red.50" border="1px" borderColor="red.200" borderRadius="md" p={4}>
            <Text color="red.600" fontSize="sm">{error}</Text>
          </Box>
        </Box>
      )}

      <Box maxW="7xl" mx="auto" px={4}>
        <Stack direction={{ base: 'column', lg: 'row' }} spacing={6} align="start">
          
          {/* Mobile-First Preview Section */}
          <Box flex="1" order={{ base: 1, lg: 2 }}>
            <Box bg="white" borderRadius="xl" p={6} boxShadow="lg" border="1px" borderColor="gray.200">
              <Flex align="center" mb={4}>
                <Box bg="blue.100" p={2} borderRadius="md" mr={3}>
                  <Text fontSize="20px">👀</Text>
                </Box>
                <Box>
                  <Heading size="md" color="gray.800">Live Preview</Heading>
                  <Text fontSize="sm" color="gray.600">See your design update in real-time</Text>
                </Box>
              </Flex>
              
              <Box 
                bg="gray.50" 
                borderRadius="lg" 
                p={4} 
                minH={{ base: "300px", md: "400px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                border="2px dashed"
                borderColor="gray.200"
              >
                {previewLoading && (
                  <Stack align="center" spacing={4}>
                    <Spinner thickness="4px" speed="0.65s" size="xl" color="blue.500" />
                    <Text className="typing-effect" color="blue.600" fontWeight="medium" textAlign="center">
                      {loadingMessage}
                    </Text>
                  </Stack>
                )}
                {!previewLoading && (
                  <ChakraImage
                    src={previewURL || templateThumbnail}
                    alt="Your Custom Design"
                    objectFit="contain"
                    borderRadius="lg"
                    maxH="100%"
                    maxW="100%"
                    shadow="md"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    style={{ 
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                  />
                )}
              </Box>
              
              <Box mt={6}>
                <Stack spacing={3}>
                  <ChakraButton
                    onClick={() => handleSubmit('save')}
                    colorScheme="gray"
                    variant="outline"
                    size="lg"
                    w="full"
                    isLoading={loading && saveAction === 'save'}
                    isDisabled={loading || cesdkStatus !== 'ready'}
                    loadingText="Saving..."
                    leftIcon={<Text fontSize="16px">💾</Text>}
                    py={6}
                    fontSize="lg"
                    fontWeight="semibold"
                    _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
                  >
                    {loading && saveAction === 'save' ? 'Saving Design...' : cesdkStatus !== 'ready' ? 'Preparing Editor...' : 'Save Design'}
                  </ChakraButton>
                  
                  <ChakraButton
                    onClick={() => handleSubmit('saveAndCart')}
                    colorScheme="blue"
                    size="lg"
                    w="full"
                    isLoading={loading && saveAction === 'saveAndCart'}
                    isDisabled={loading || cesdkStatus !== 'ready'}
                    loadingText="Saving & Adding..."
                    leftIcon={<Text fontSize="16px">🛒</Text>}
                    py={6}
                    fontSize="lg"
                    fontWeight="semibold"
                  >
                    {loading && saveAction === 'saveAndCart' ? 'Saving & Adding to Cart...' : cesdkStatus !== 'ready' ? 'Preparing Editor...' : 'Save & Add to Cart'}
                  </ChakraButton>
                </Stack>
                
                <Text fontSize="xs" color="gray.500" textAlign="center" mt={3}>
                  {saveAction === 'saveAndCart' || loading ? 
                    'Your design will be saved and added to your cart for purchase' : 
                    'Your design will be saved to your Pro Edits gallery'
                  }
                </Text>
              </Box>
            </Box>
          </Box>

          {/* Mobile-First Customization Panel */}
          <Box flex="1" order={{ base: 2, lg: 1 }}>
            <Stack spacing={6}>
              
              {/* Step 1: Select Your Photo */}
              {placeholders.some(p => ['source', 'person'].includes(p.name.toLowerCase())) && (
                <Box bg="white" borderRadius="xl" p={6} boxShadow="lg" border="1px" borderColor="gray.200">
                  <Flex align="center" mb={4}>
                    <Box bg="green.100" p={2} borderRadius="md" mr={3}>
                      <Text fontSize="20px">📸</Text>
                    </Box>
                    <Box flex="1">
                      <Heading size="md" color="gray.800">Step 1: Choose Your Photo</Heading>
                      <Text fontSize="sm" color="gray.600">Select which athlete photo to use in your design</Text>
                    </Box>
                  </Flex>
                  
                  {mediaDayImages.length === 0 ? (
                    <Box bg="yellow.50" border="1px" borderColor="yellow.200" borderRadius="lg" p={4} textAlign="center">
                      <Text fontSize="16px" mb={2}>📷</Text>
                      <Text color="yellow.800" fontWeight="medium" mb={1}>No photos uploaded yet</Text>
                      <Text fontSize="sm" color="yellow.700">Upload athlete photos first to customize this template</Text>
                      <ChakraButton
                        mt={4}
                        colorScheme="blue"
                        onClick={() => fileInputRef.current.click()}
                        isLoading={uploadingImage}
                        leftIcon={<Text fontSize="16px">⬆️</Text>}
                      >
                        {uploadingImage ? 'Uploading...' : 'Upload Photo'}
                      </ChakraButton>
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleCustomizeUpload(f);
                          e.target.value = '';
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Text fontSize="sm" color="gray.600" mb={4}>
                        Tap any photo below to use it in your design:
                      </Text>
                      <Box
                        display="grid"
                        gridTemplateColumns={{ 
                          base: "repeat(2, 1fr)", 
                          sm: "repeat(3, 1fr)", 
                          md: "repeat(4, 1fr)" 
                        }}
                        gap={4}
                        sx={{
                          '& > *:nth-of-type(3n+1)': {
                            gridRowEnd: 'span 2'
                          },
                          '& > *:nth-of-type(7n+2)': {
                            gridRowEnd: 'span 2'
                          }
                        }}
                      >
                        {mediaDayImages.map((url, idx) => (
                          <Box
                            as="button"
                            type="button"
                            key={idx}
                            onClick={() => setSelectedImage(url)}
                            borderRadius="xl"
                            overflow="hidden"
                            position="relative"
                            border="3px solid"
                            borderColor={selectedImage === url ? 'blue.500' : 'gray.200'}
                            bg={selectedImage === url ? 'blue.50' : 'white'}
                            _hover={{ 
                              borderColor: 'blue.400', 
                              transform: 'translateY(-2px) scale(1.02)',
                              boxShadow: 'lg'
                            }}
                            _active={{ transform: 'translateY(0) scale(0.98)' }}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            _focus={{ boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.3)' }}
                            minH={{ base: "120px", md: "140px" }}
                            p={2}
                          >
                            <ChakraImage 
                              src={url} 
                              alt="Athlete photo" 
                              objectFit="contain"
                              w="100%" 
                              h="100%"
                              maxH={{ base: "200px", md: "250px" }}
                              borderRadius="lg"
                            />
                            {selectedImage === url && (
                              <>
                                <Box
                                  position="absolute"
                                  top="3"
                                  right="3"
                                  bg="blue.500"
                                  color="white"
                                  borderRadius="full"
                                  w="24px"
                                  h="24px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  fontSize="sm"
                                  fontWeight="bold"
                                  boxShadow="md"
                                >
                                  ✓
                                </Box>
                                <Box
                                  position="absolute"
                                  inset="0"
                                  bg="blue.500"
                                  opacity="0.1"
                                  borderRadius="lg"
                                />
                              </>
                            )}
                            <Box
                              position="absolute"
                              bottom="2"
                              left="2"
                              right="2"
                              bg="blackAlpha.600"
                              color="white"
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="md"
                              opacity={selectedImage === url ? 1 : 0}
                              transition="opacity 0.2s"
                            >
                              Selected
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      {selectedImage && (
                        <Box bg="green.50" border="1px" borderColor="green.200" borderRadius="lg" p={3} mt={3}>
                          <Text fontSize="sm" color="green.800">
                            ✓ Photo selected! Your design will update automatically.
                          </Text>
                        </Box>
                      )}
                      <Box mt={2}>
                        <Text fontSize="xs" color="gray.500" display="inline">or </Text>
                        <ChakraButton
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => fileInputRef.current.click()}
                          isLoading={uploadingImage}
                        >
                          {uploadingImage ? 'Uploading...' : 'Upload New Photo'}
                        </ChakraButton>
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleCustomizeUpload(f);
                            e.target.value = '';
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {/* Step 2: Customize Text */}
              {placeholders.some(p => p.type === '//ly.img.ubq/text') && (
                <Box bg="white" borderRadius="xl" p={6} boxShadow="lg" border="1px" borderColor="gray.200">
                  <Flex align="center" mb={4}>
                    <Box bg="purple.100" p={2} borderRadius="md" mr={3}>
                      <Text fontSize="20px">✏️</Text>
                    </Box>
                    <Box>
                      <Heading size="md" color="gray.800">Step 2: Add Your Details</Heading>
                      <Text fontSize="sm" color="gray.600">Personalize the text on your design</Text>
                    </Box>
                  </Flex>
                  
                  <Stack spacing={4}>
                    {placeholders.map((placeholder) => {
                      if (placeholder.type === '//ly.img.ubq/text') {
                        return (
                          <FormControl key={placeholder.name} isDisabled={loading}>
                            <FormLabel color="gray.700" fontWeight="medium" fontSize="sm">
                              {placeholder.name}
                              <Text as="span" color="gray.500" fontWeight="normal" ml={1}>
                                (appears on your design)
                              </Text>
                            </FormLabel>
                            <Input
                              bg="gray.50"
                              border="2px solid"
                              borderColor="gray.200"
                              _hover={{ borderColor: 'gray.300' }}
                              _focus={{ borderColor: 'blue.500', bg: 'white' }}
                              _placeholder={{ color: 'gray.400' }}
                              name={placeholder.name}
                              value={formData[placeholder.name] || ''}
                              onChange={handleInputChange}
                              placeholder={`Enter ${placeholder.name.toLowerCase()}`}
                              size="lg"
                              borderRadius="lg"
                            />
                          </FormControl>
                        );
                      }
                      return null;
                    })}
                  </Stack>
                  
                  <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="lg" p={3} mt={4}>
                    <Text fontSize="sm" color="blue.800">
                      💡 <strong>Tip:</strong> Changes appear instantly in your preview above!
                    </Text>
                  </Box>
                </Box>
              )}

              {/* Help/Tips Section */}
              <Box bg="gradient" bgGradient="linear(135deg, blue.500, purple.600)" borderRadius="xl" p={6} color="white">
                <Flex align="center" mb={3}>
                  <Text fontSize="20px" mr={2}>🎯</Text>
                  <Heading size="sm">Quick Tips</Heading>
                </Flex>
                <Stack spacing={2} fontSize="sm">
                  <Text>• Select a high-quality athlete photo for best results</Text>
                  <Text>• Keep text short and punchy for maximum impact</Text>
                  <Text>• Your design updates automatically as you make changes</Text>
                  <Text>• Save when you're happy with your preview</Text>
                </Stack>
              </Box>
              
            </Stack>
          </Box>
          
        </Stack>
      </Box>

      {/* Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="white" borderRadius="xl" mx={4}>
          <ModalHeader textAlign="center" pb={2}>
            <VStack spacing={2}>
              <Box bg="green.100" p={3} borderRadius="full">
                <Text fontSize="24px">✅</Text>
              </Box>
              <Heading size="md" color="gray.800">Design Created Successfully!</Heading>
              <Text fontSize="sm" color="gray.600" fontWeight="normal">
                Your custom design is ready {saveAction === 'saveAndCart' ? 'and has been added to your cart' : ''}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalBody display="flex" flexDirection="column" alignItems="center" pb={2}>
            {exportURL && (
              <Box borderRadius="lg" overflow="hidden" boxShadow="lg" border="1px" borderColor="gray.200">
                <ChakraImage 
                  src={exportURL} 
                  alt="Your Custom Design" 
                  maxH="400px" 
                  objectFit="contain"
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center" pt={2}>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} w="full" maxW="sm">
              <ChakraButton 
                variant="outline" 
                onClick={onClose}
                size="lg"
                flex="1"
              >
                Close
              </ChakraButton>
              {saveAction === 'saveAndCart' ? (
                <ChakraButton 
                  colorScheme="blue" 
                  onClick={() => navigate('/cart')}
                  size="lg"
                  flex="1"
                  leftIcon={<Text fontSize="14px">🛒</Text>}
                >
                  View Cart
                </ChakraButton>
              ) : (
                <ChakraButton 
                  colorScheme="blue" 
                  onClick={() => navigate('/dashboard')}
                  size="lg"
                  flex="1"
                  leftIcon={<Text fontSize="14px">📁</Text>}
                >
                  View in Gallery
                </ChakraButton>
              )}
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hidden editor container (off-screen) */}
      <Box ref={editorContainerRef} position="absolute" left="-99999px" w="1px" h="1px" overflow="hidden" />
    </Box>
  );
};

export default CustomizationPage;